/**
 * Session client (issues #19, #45).
 *
 * The refresh token is httpOnly — this module never sees it. What the client
 * handles is everything around it: exchanging an issued token for a token pair,
 * asking "who am I", and mapping the answer onto the SAME identity store the
 * dev switcher writes, so every downstream mechanism (auth headers, scoped
 * query keys, `installIdentityCacheReset`) works identically in both modes.
 *
 * The server ignores `x-user-id` / `x-role` whenever a bearer token is present,
 * so the stored identity's only wire-relevant field in session mode is the
 * SELECTED TENANT — which is exactly the half of (user × tenant × role) that
 * is legitimately the client's to choose.
 */
import type { LoginResponse, SessionResponse, SessionUser, TenantSummary } from '@cusp/contracts';
import { ApiError } from './apiError.js';
import { clearTokens, getAccessToken, refreshAccessToken, setTokens } from './accessToken.js';
import { getIdentity, setIdentity, type Identity } from './identity.js';
import { request } from './request.js';

/**
 * The current session, or null when there is none. Other errors propagate.
 *
 * Access tokens live in memory, so a page load starts with none — hence the
 * explicit refresh before asking. `request()` would recover from the 401 on its
 * own, but doing it here keeps the common path at one round trip instead of
 * two, and makes "no cookie either" a null without an error ever being thrown.
 */
export async function fetchSession(): Promise<SessionResponse | null> {
  if (getAccessToken() === null && (await refreshAccessToken()) === null) return null;

  try {
    return await request<SessionResponse>('/v1/auth/session');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

/**
 * Exchange the token from a login link for a token pair. 401 =
 * invalid/expired/revoked link.
 *
 * The access token goes straight into memory; the refresh token never arrives
 * here at all — it is a `Set-Cookie` the browser stores and this code cannot
 * read. That asymmetry is the XSS mitigation, so keep it.
 */
export async function loginWithToken(token: string): Promise<SessionResponse> {
  const body = await request<LoginResponse>('/v1/auth/login', {
    method: 'POST',
    body: { token, client: 'web' },
    credentialExchange: true,
  });
  setTokens(body.tokens);
  return { user: body.user, memberships: body.memberships };
}

/**
 * Exchange a Google ID token (collected by GIS in the browser) for a session
 * (issue #89). 401 = the verifier refused it, uniformly — an invalid token and
 * an email bound to a different Google account read identically on purpose.
 *
 * Token handling is byte-for-byte `loginWithToken`'s: access token to memory,
 * refresh token arriving only as an httpOnly Set-Cookie this code never sees.
 * A brand-new account is just a session whose membership list is empty.
 */
export async function loginWithGoogle(idToken: string): Promise<SessionResponse> {
  const body = await request<LoginResponse>('/v1/auth/google', {
    method: 'POST',
    body: { idToken, client: 'web' },
    credentialExchange: true,
  });
  setTokens(body.tokens);
  return { user: body.user, memberships: body.memberships };
}

/**
 * Revoke the session server-side, then drop the local credentials and identity
 * — which also repartitions the query cache, exactly as a dev identity switch
 * does. Both are cleared even if the network call fails: a user who pressed
 * "sign out" on a flaky connection must not stay signed in locally.
 */
export async function logout(): Promise<void> {
  try {
    await request<undefined>('/v1/auth/logout', { method: 'POST' });
  } finally {
    clearTokens();
    setIdentity(null);
  }
}

/**
 * Switch acting identity onto a tenant an access code just granted (issue #92).
 *
 * Redemption changes what memberships you hold, never WHO you are, so the
 * signed-in user is passed straight through and only the tenant is new. This is
 * the SAME switch the login picker performs (`membershipIdentity` + the shared
 * `setIdentity`), reused rather than reinvented: `setIdentity` fires
 * `installIdentityCacheReset`, which cancels in-flight requests carrying the old
 * headers and drops the previous scope's cache, so the granted tenant's courses
 * refetch clean. `tenant` carries the fresh role the redeem route returned.
 */
export function adoptRedeemedIdentity(user: SessionUser, tenant: TenantSummary): void {
  setIdentity(membershipIdentity({ user, memberships: [tenant] }, tenant));
}

/** One membership, flattened onto the identity store's shape. */
export function membershipIdentity(session: SessionResponse, m: TenantSummary): Identity {
  return {
    tenantId: m.tenantId,
    tenantName: m.name,
    tenantSlug: m.slug,
    userId: session.user.id,
    userName: session.user.name,
    email: session.user.email,
    role: m.role,
  };
}

export type AdoptResult = 'kept' | 'selected' | 'needs-picker';

/**
 * Reconcile the stored identity with a live session.
 *
 * - A stored identity naming a membership the session actually holds is
 *   re-written from the fresh row (an admin may have changed the role).
 * - A stored identity the session does NOT hold — a stale dev pick, another
 *   person's localStorage — is dropped, never trusted.
 * - With exactly one membership there is nothing to pick; select it.
 * - With several and no valid selection, the caller renders the picker.
 */
export function adoptSessionIdentity(session: SessionResponse): AdoptResult {
  const current = getIdentity();
  const held = current
    ? session.memberships.find(
        (m) => m.tenantId === current.tenantId && session.user.id === current.userId,
      )
    : undefined;

  if (held) {
    setIdentity(membershipIdentity(session, held));
    return 'kept';
  }
  const sole = session.memberships.length === 1 ? session.memberships[0] : undefined;
  if (sole) {
    setIdentity(membershipIdentity(session, sole));
    return 'selected';
  }
  setIdentity(null);
  return 'needs-picker';
}
